import type { getMutualFundDetailContentResponse } from '#/api/generated/content/content';
import { useGetMutualFundDetailContent } from '#/api/generated/content/content';
import type { ContentPage } from '#/api/generated/fINRAMarketDataAPI.schemas';
import type {
  ContentArticleCard,
  DefinitionTerm,
  StaticFallback,
} from '#/content/lib';
import {
  contentArticleCards,
  contentText,
  isContentPage,
  normalizeDefinitions,
} from '#/content/lib';
import { CONTENT_CACHE_QUERY_POLICY } from '#/content/queries';
import { shouldUseFallbackContent } from '#/lib/api-client';

/**
 * The mutual-fund-detail content document: view model, fallback copy, merge,
 * and hook. Section intros are Drupal rich text destined for `RichText`, so
 * they carry their HTML through unstripped. Glossary terms (tooltips + stat
 * descriptions) are returned separately as a slug lookup — see
 * {@link normalizeDefinitions}.
 */
export interface MutualFundDetailContentView {
  priceCostIntro: string;
  riskIntro: string;
  portfolioIntro: string;
  topHoldingsIntro: string;
  learnMoreArticles: ContentArticleCard[] | null;
}

/**
 * Tier 1 fallback mutual-fund-detail copy, never null
 */
export const MUTUAL_FUND_DETAIL_FALLBACK: StaticFallback<MutualFundDetailContentView> =
  {
    priceCostIntro:
      "All mutual funds have fees, with some charged at specific times, based on actions you take, and some are charged on an ongoing basis. Each fund's prospectus describes its fees in detail.",
    riskIntro:
      'These risk metrics are provided to contribute to a fuller picture of the risk of the security. Note that historical performance does not necessarily indicate future performance.',
    portfolioIntro:
      "A mutual fund's portfolio composition reveals its diversification strategy through the specific allocation of assets across different asset classes and sectors. This breakdown helps to illustrate the fund's risk and alignment with investment goals by showing exactly where the money is being deployed in the market.",
    topHoldingsIntro:
      'These investment products are the top holdings of this fund. They are provided as informational, and may be part of an investment strategy for portfolio diversification.',
  };

/**
 * Merge fields and fallbacks, null is the total-failure state.
 *
 * The risk intro reads `risk_section_intro_f` — the fund-specific field —
 * never `risk_section_intro`, which belongs to other detail documents.
 */
export function mergeMutualFundDetail(
  page: ContentPage | null | undefined,
): MutualFundDetailContentView {
  return {
    priceCostIntro:
      contentText(page?.price_cost_section_intro) ??
      MUTUAL_FUND_DETAIL_FALLBACK.priceCostIntro,
    riskIntro:
      contentText(page?.risk_section_intro_f) ??
      MUTUAL_FUND_DETAIL_FALLBACK.riskIntro,
    portfolioIntro:
      contentText(page?.portfolio_section_intro) ??
      MUTUAL_FUND_DETAIL_FALLBACK.portfolioIntro,
    topHoldingsIntro:
      contentText(page?.top_holdings_section_intro) ??
      MUTUAL_FUND_DETAIL_FALLBACK.topHoldingsIntro,
    learnMoreArticles: contentArticleCards(page, 'learn_more_articles'),
  };
}

interface UseMutualFundDetailContentResult {
  content: MutualFundDetailContentView;
  /** Glossary terms for the page, keyed by slug (empty on failure). */
  definitions: Map<string, DefinitionTerm>;
  /** True when the content API failed and fallback copy is being shown. */
  isDegraded: boolean;
}

interface MutualFundDetailContentData {
  content: MutualFundDetailContentView;
  definitions: Map<string, DefinitionTerm>;
}

/**
 * Stable fallback returned while the query is unsettled or failed. Reused by
 * reference so the detail view's seed effect doesn't loop on a fresh Map.
 */
const FALLBACK_CONTENT_DATA: MutualFundDetailContentData = {
  content: mergeMutualFundDetail(null),
  definitions: normalizeDefinitions(null),
};

function selectMutualFundDetailContent(
  response: getMutualFundDetailContentResponse,
): MutualFundDetailContentData {
  const page = isContentPage(response.data) ? response.data : null;
  return {
    content: mergeMutualFundDetail(page),
    definitions: normalizeDefinitions(page),
  };
}

/**
 * Mutual-fund-detail content, merged against fallback copy.
 *
 * Warmed via `mutualFundDetailContentOptions()` on `/$category/$id` with
 * `staleTime: Infinity` for no re-renders for the life of the session.
 */
export function useMutualFundDetailContent(): UseMutualFundDetailContentResult {
  const query = useGetMutualFundDetailContent({
    query: {
      ...CONTENT_CACHE_QUERY_POLICY,
      select: selectMutualFundDetailContent,
    },
  });

  const data = query.isSuccess ? query.data : FALLBACK_CONTENT_DATA;

  return {
    content: data.content,
    definitions: data.definitions,
    isDegraded: shouldUseFallbackContent(query.error),
  };
}
